import { useState, useEffect, useRef } from 'react'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import Input from '@mui/material/Input'
import Typography from '@mui/material/Typography'
import { Paper } from '@components'
import { colors } from '@global/constants'
import { Theme } from 'Theme'

function validateText(value, validationOptions = {}, governanceOwner, required) {
  const checks = []
  if (!value) {
    value = ''
  }

  const hasMinLength = !!governanceOwner.min_length
  const hasMaxLength = !!governanceOwner.max_length
  const valueMustBeExactLength =
    hasMinLength && hasMaxLength && governanceOwner.max_length === governanceOwner.min_length

  if (required) {
    checks.push({
      value: !!value,
      message: 'This field must contain a value.',
    })
  }

  if (validationOptions.begins_with) {
    const passesBeginsWithTest = value.startsWith(validationOptions.begins_with)
    checks.push({
      value: passesBeginsWithTest,
      message: `Value must begin with ${validationOptions.begins_with}`,
    })
  }

  if (validationOptions.ends_with) {
    const passesEndsWithTest = value.endsWith(validationOptions.ends_with)
    checks.push({
      value: passesEndsWithTest,
      message: `Value must end with ${validationOptions.ends_with}`,
    })
  }

  if (valueMustBeExactLength) {
    checks.push({
      value: governanceOwner.max_length === value.length,
      message: `This field must be exactly ${governanceOwner.max_length} characters.`,
    })
  }

  if (!valueMustBeExactLength && governanceOwner.max_length) {
    checks.push({
      value: value.length <= governanceOwner.max_length,
      message: `This field must be no more than ${governanceOwner.max_length} characters.`,
    })
  }

  if (!valueMustBeExactLength && governanceOwner.min_length) {
    checks.push({
      value: value.length >= governanceOwner.min_length,
      message: `This field must contain at least ${governanceOwner.min_length} characters.`,
    })
  }

  if (validationOptions.contains) {
    const passesContainsTest = value.includes(validationOptions.contains)
    checks.push({
      value: passesContainsTest,
      message: `Value must contain ${validationOptions.contains}`,
    })
  }

  if (validationOptions.does_not_contain) {
    let passesDoesNotContainTest = true
    if (validationOptions.exclude_as_string) {
      passesDoesNotContainTest = !value.includes(validationOptions.does_not_contain)
    } else {
      // we have to text every string.
      const checks = validationOptions.does_not_contain.split('')
      checks.forEach(checker => {
        if (value.includes(checker)) {
          passesDoesNotContainTest = false
        }
      })
    }
    if (!passesDoesNotContainTest) {
      // worried if we break this out into many, it could get too long. For now w'ell do this.
      checks.push({
        value: false,
        message: `Value must not contain ${validationOptions.does_not_contain}`,
      })
    } else {
      checks.push({ value: true, message: "Value doesn't contain forbidden values" })
    }
  }

  // TODO: what about this one? It's not an error. It's just a warning.
  // if (validationOptions.flag_new) {
  //   const valueIsNew =
  //     governanceOwner.previous_values && governanceOwner.previous_values.indexOf(value) === -1
  //   if (valueIsNew) {
  //     warnings.push(`This value has not been submitted previously.`)
  //   }
  // }

  if (validationOptions.prohibit_letters) {
    const valueHasLetters = /[a-z]/gi.test(value)
    checks.push({ value: !valueHasLetters, message: `Value must not contain letters` })
  }

  if (validationOptions.prohibit_numbers) {
    const valueHasNumbers = /[0-9]/g.test(value)
    checks.push({ value: !valueHasNumbers, message: `Value must not contain numbers` })
  }

  if (validationOptions.prohibit_spaces) {
    const valueHasSpaces = /\s/g.test(value)
    checks.push({ value: !valueHasSpaces, message: `Value must not contain spaces` })
  }

  if (validationOptions.prohibit_symbols) {
    const valueHasSymbols = /[^a-zA-Z0-9 ]/g.test(value)
    checks.push({ value: !valueHasSymbols, message: `Value must not contain symbols` })
  }

  if (!checks.length && !hasMinLength && !hasMaxLength) {
    checks.push({
      value: true,
      message: `Any value is acceptable for this field`,
    })
  }

  return checks
}

export const TextEdit = props => {
  const [newValue, setNewValue] = useState(props.data || '')
  const [height] = useState(props.parent.clientHeight)
  const [validations, setValidations] = useState(
    validateText(props.data, props.governance.validation, props.governance),
  )
  const inputRef = useRef(null)

  const maxLength = props.governance.max_length

  const isRequired = props.templateField.required || props.governance.required

  useEffect(() => {
    props.updateData(newValue)
    setValidations(
      validateText(newValue, props.governance.validation, props.governance, isRequired),
    )
  }, [newValue])

  useEffect(() => {
    inputRef.current.firstChild.focus()
    inputRef.current.firstChild.setSelectionRange(
      inputRef.current.firstChild.value.length,
      inputRef.current.firstChild.value.length,
    )
  }, [])

  const checkForEnter = e => {
    if (e.key === 'Enter') {
      e.stopPropagation()
      e.preventDefault()
      props.updateData(newValue)
      props.close(true)
    }
    if (e.key === 'Escape') {
      e.stopPropagation()
      e.preventDefault()
      props.updateData(props.data)
      props.close()
    }
    if (e.key === 'Tab') {
      e.stopPropagation()
      e.preventDefault()
      props.updateData(newValue)
      props.close('TAB')
    }
  }

  return (
    <Theme>
      <Input
        value={newValue}
        autoFocus={true}
        ref={inputRef}
        onKeyDown={checkForEnter}
        onChange={e => {
          setNewValue(e.target.value)
        }}
        style={{
          boxSizing: 'border-box',
          height: 'initial',
          minHeight: height,
          width: '100%',
          backgroundColor: 'white',
          position: 'relative',
          paddingLeft: 4,
          paddingRight: 4,
          border: '1px solid #25515B',
          fontSize: 12.25,
        }}
        multiline={true}
        disableUnderline={true}
        data-testid="textarea-wrapper"
        slotProps={{
          input: {
            'data-testid': 'textarea',
          },
        }}
      />
      <div style={{ position: 'absolute', width: '100%' }}>
        <Paper elevation={5} data-testid="dropdown">
          <div style={{ display: 'flex', flexDirection: 'column', padding: 12, gap: 10 }}>
            {validations.map(v => (
              <div key={v.message} style={{ display: 'flex', alignItems: 'center' }}>
                {v.value ? (
                  <CheckIcon style={{ marginRight: 8, color: colors.green.main }} />
                ) : (
                  <CloseIcon style={{ marginRight: 8, color: colors.red.main }} />
                )}
                <Typography variant="body2" style={{ color: 'black', lineHeight: '11px' }}>
                  {v.message}
                </Typography>
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #ccc', padding: 12 }}>
            <Typography variant="caption" style={{ color: colors.gray.dark }}>
              LENGTH: {newValue.length}
              {maxLength && ` / ${maxLength}`}
            </Typography>
          </div>
        </Paper>
      </div>
    </Theme>
  )
}
